<template>
  <div class="py-4 container-fluid">
    <div class="card">
      <div class="card-header pb-0">
        <div class="d-flex align-items-center mb-3">
          <h6 class="mb-0">Shopify — Inventario</h6>
          <argon-button
            color="primary"
            size="sm"
            class="ms-auto"
            :disabled="isSyncing"
            @click="syncProducts"
          >
            <span v-if="isSyncing">
              <i class="fas fa-spinner fa-spin me-1"></i>
              Sincronizando...
            </span>
            <span v-else>Sincronizar</span>
          </argon-button>
        </div>
        <div class="row">
          <div class="col-md-6 mb-3">
            <label for="searchQuery" class="mb-1">Buscar</label>
            <input
              id="searchQuery"
              v-model="searchQuery"
              type="text"
              class="form-control"
              placeholder="Buscar por título, handle, vendor o ID..."
              @input="onSearchInput"
            />
          </div>
        </div>
      </div>
      <div class="card-body px-0 pt-0 pb-2">
        <div v-if="isLoading" class="text-center py-4">
          <i class="fas fa-spinner fa-spin me-1"></i>
          Cargando inventario...
        </div>

        <p v-else-if="!products.length" class="text-muted px-4 mb-0">
          No hay productos sincronizados. Pulsa Sincronizar para traerlos desde Shopify.
        </p>

        <div v-else class="table-responsive">
          <table class="table align-items-center mb-0">
            <thead>
              <tr>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-4">
                  ID Shopify
                </th>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Producto
                </th>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Estado
                </th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Variantes
                </th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Stock
                </th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Precio
                </th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Sincronizado
                </th>
                <th v-if="shopifyEnabled" class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Opciones
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="product in products" :key="product.id">
                <td class="ps-4">
                  <span class="text-sm">{{ product.shopify_product_id }}</span>
                </td>
                <td>
                  <div class="d-flex align-items-center">
                    <img
                      v-if="product.image_url"
                      :src="product.image_url"
                      :alt="product.title"
                      class="avatar avatar-sm me-2"
                    />
                    <div>
                      <span class="text-sm font-weight-bold d-block">{{ product.title }}</span>
                      <span v-if="product.vendor" class="text-xs text-secondary">{{ product.vendor }}</span>
                    </div>
                  </div>
                </td>
                <td>
                  <span
                    class="badge badge-sm"
                    :class="product.status === 'active' ? 'bg-gradient-success' : 'bg-gradient-secondary'"
                  >
                    {{ product.status }}
                  </span>
                </td>
                <td class="text-center">
                  <span class="text-sm">{{ product.variants_count }}</span>
                </td>
                <td class="text-center">
                  <span class="text-sm">{{ product.inventory_quantity }}</span>
                </td>
                <td class="text-center">
                  <span class="text-sm">{{ formatPriceRange(product) }}</span>
                </td>
                <td class="text-center">
                  <span class="text-xs text-secondary">{{ formatDate(product.synced_at) }}</span>
                </td>
                <td v-if="shopifyEnabled" class="text-center">
                  <div class="d-inline-flex align-items-center opciones-icons">
                    <span class="badge badge-sm bg-gradient-info me-1" title="Colombia">COL</span>
                    <button
                      class="btn btn-sm btn-link p-0 text-success opciones-icon-btn"
                      title="Sincronizar con Shopify (COL)"
                      :disabled="pushingId === product.id"
                      @click="pushProductToShopify(product)"
                    >
                      <i v-if="pushingId === product.id" class="fas fa-spinner fa-spin"></i>
                      <i v-else class="fab fa-shopify"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <div class="pagination p-3">
            <a
              v-for="link in links"
              :key="link.label"
              class="page-item cursor-pointer"
              :class="{ active: link.active, disabled: !link.url }"
              @click="link.url && fetchInventoryFromLink(link.url)"
            >
              {{ link.label }}
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ArgonButton from "@/components/ArgonButton.vue";
import shopifyService from "@/services/shopify.service";
import Swal from "sweetalert2";

export default {
  name: "ShopifyInventory",
  components: {
    ArgonButton,
  },
  data() {
    return {
      isSyncing: false,
      isLoading: false,
      products: [],
      links: [],
      currentPage: 1,
      lastPage: 1,
      total: 0,
      searchQuery: "",
      searchTimeout: null,
      pushingId: null,
    };
  },
  computed: {
    shopifyEnabled() {
      return this.$store.getters["settings/shopifyEnabled"];
    },
  },
  mounted() {
    this.fetchInventory();
  },
  methods: {
    onSearchInput() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.fetchInventory(1);
      }, 400);
    },
    async fetchInventory(page = 1) {
      this.isLoading = true;
      try {
        const response = await shopifyService.getInventory({
          page,
          search: this.searchQuery,
          per_page: 15,
        });
        const data = response?.data || {};

        this.products = data.data || [];
        this.links = data.links || [];
        this.currentPage = data.current_page || 1;
        this.lastPage = data.last_page || 1;
        this.total = data.total || 0;
      } catch (error) {
        console.error("Error al cargar inventario Shopify:", error);
        await Swal.fire(
          "Error",
          error?.response?.data?.message || "No se pudo cargar el inventario.",
          "error"
        );
      } finally {
        this.isLoading = false;
      }
    },
    fetchInventoryFromLink(url) {
      const page = new URL(url).searchParams.get("page") || 1;
      this.fetchInventory(Number(page));
    },
    async syncProducts() {
      this.isSyncing = true;
      try {
        const response = await shopifyService.syncProducts();
        const data = response?.data || {};

        if (data.success) {
          const sync = data.sync || {};
          await this.fetchInventory(1);
          await Swal.fire(
            "Sincronizado",
            `Se procesaron ${sync.total ?? data.products?.length ?? 0} productos (${sync.created ?? 0} nuevos, ${sync.updated ?? 0} actualizados).`,
            "success"
          );
        } else {
          throw new Error(data.message || "No se pudo sincronizar.");
        }
      } catch (error) {
        console.error("Error al sincronizar Shopify:", error);
        await Swal.fire(
          "Error",
          error?.response?.data?.message || error.message || "Error al sincronizar con Shopify.",
          "error"
        );
      } finally {
        this.isSyncing = false;
      }
    },
    formatPriceRange(product) {
      const min = product.price_min;
      const max = product.price_max;

      if (min == null && max == null) {
        return "—";
      }

      const formatter = new Intl.NumberFormat("es-CO", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });

      if (min != null && max != null && Number(min) !== Number(max)) {
        return `${formatter.format(min)} – ${formatter.format(max)}`;
      }

      return formatter.format(min ?? max);
    },
    async pushProductToShopify(product) {
      this.pushingId = product.id;
      try {
        const response = await shopifyService.pushProductToShopify(product.id);
        const data = response?.data || {};

        if (data.success) {
          const action = data.action || "sincronizado";
          await Swal.fire({
            icon: "success",
            title: "Sincronizado con Shopify (COL)",
            text: `«${product.title}» fue ${action} correctamente en Shopify.`,
            timer: 2000,
            showConfirmButton: false,
          });
        } else {
          throw new Error(data.message || "No se pudo sincronizar con Shopify.");
        }
      } catch (error) {
        console.error("Error al sincronizar producto con Shopify:", error);
        await Swal.fire(
          "Error",
          error?.response?.data?.message || error.message || "Error al sincronizar con Shopify.",
          "error"
        );
      } finally {
        this.pushingId = null;
      }
    },
    formatDate(value) {
      if (!value) {
        return "—";
      }

      return new Date(value).toLocaleString("es-CO", {
        dateStyle: "short",
        timeStyle: "short",
      });
    },
  },
};
</script>

<style scoped>
.pagination {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 20px;
}

.page-item {
  margin: 0 5px;
  padding: 4px 8px;
  user-select: none;
}

.page-item.disabled {
  opacity: 0.5;
  pointer-events: none;
}

.active {
  font-weight: bold;
}

.opciones-icons {
  gap: 8px;
  vertical-align: middle;
}

.opciones-icon-btn {
  margin-bottom: 0 !important;
  line-height: 1;
  font-size: 1rem;
}
</style>
